class AnnouncementBarCarousel extends HTMLElement {
  connectedCallback() {
    this.slider = this.querySelector('[data-announcement-slider]');
    if (!this.slider) return;

    this.slides = [...this.querySelectorAll('[data-announcement-slide]')].filter((slide) =>
      slide.querySelector('.announcement-bar__message')?.textContent?.trim()
    );

    if (!this.slides.length) return;

    this.autoplayEnabled = this.slider.getAttribute('data-autoplay') !== 'false';
    this.speed = (parseInt(this.slider.dataset.speed, 10) || 5) * 1000;
    this.animationDelay = 250;
    this.currentIndex = 0;
    this.timer = null;
    this.isAnimating = false;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.bindEvents();
    this.showSlide(0);
    this.setAttribute('data-initialized', 'true');
    this.syncHeight();
    requestAnimationFrame(() => this.syncHeight());
    initAnnouncementCountdowns(this);

    if (this.slides.length < 2 || !this.autoplayEnabled) return;

    if (!this.reducedMotion.matches) this.play();
  }

  bindEvents() {
    this.onMouseEnter = this.pause.bind(this);
    this.onMouseLeave = this.play.bind(this);
    this.onFocusIn = (event) => {
      if (this.contains(event.target)) this.pause();
    };
    this.onFocusOut = (event) => {
      if (!this.contains(event.relatedTarget)) this.play();
    };
    this.onReducedMotionChange = () => {
      if (this.reducedMotion.matches) this.pause();
      else this.play();
    };
    this.onResize = () => this.syncHeight();

    this.addEventListener('mouseenter', this.onMouseEnter);
    this.addEventListener('mouseleave', this.onMouseLeave);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('focusout', this.onFocusOut);
    this.reducedMotion.addEventListener('change', this.onReducedMotionChange);
    window.addEventListener('resize', this.onResize);
    this.resizeObserver = new ResizeObserver((entries) => {
      const width = Math.round(entries[0]?.contentRect?.width || 0);
      if (width === this._lastObservedWidth) return;
      this._lastObservedWidth = width;
      this.syncHeight();
    });
    this.resizeObserver.observe(this);
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => this.syncHeight());
    }
  }

  disconnectedCallback() {
    this.pause();
    this.removeEventListener('mouseenter', this.onMouseEnter);
    this.removeEventListener('mouseleave', this.onMouseLeave);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('focusout', this.onFocusOut);
    this.reducedMotion?.removeEventListener('change', this.onReducedMotionChange);
    window.removeEventListener('resize', this.onResize);
    this.resizeObserver?.disconnect();
  }

  syncHeight() {
    if (!this.slider || !this.slides.length || this._syncingHeight) return;

    this._syncingHeight = true;

    let maxHeight = 0;
    const measured = this.slides.map((slide) => {
      const wasHidden = slide.hasAttribute('hidden');
      const prev = {
        display: slide.style.display,
        position: slide.style.position,
        visibility: slide.style.visibility,
        pointerEvents: slide.style.pointerEvents,
        left: slide.style.left,
        right: slide.style.right,
        width: slide.style.width,
        height: slide.style.height,
        top: slide.style.top,
        bottom: slide.style.bottom,
      };

      slide.removeAttribute('hidden');
      slide.style.display = 'block';
      slide.style.position = 'absolute';
      slide.style.visibility = 'hidden';
      slide.style.pointerEvents = 'none';
      slide.style.left = '0';
      slide.style.right = '0';
      slide.style.top = 'auto';
      slide.style.bottom = 'auto';
      slide.style.width = '100%';
      slide.style.height = 'auto';

      maxHeight = Math.max(maxHeight, slide.getBoundingClientRect().height);

      return { slide, wasHidden, prev };
    });

    measured.forEach(({ slide, wasHidden, prev }) => {
      slide.style.display = prev.display;
      slide.style.position = prev.position;
      slide.style.visibility = prev.visibility;
      slide.style.pointerEvents = prev.pointerEvents;
      slide.style.left = prev.left;
      slide.style.right = prev.right;
      slide.style.width = prev.width;
      slide.style.height = prev.height;
      slide.style.top = prev.top;
      slide.style.bottom = prev.bottom;
      if (wasHidden) slide.setAttribute('hidden', '');
      else slide.removeAttribute('hidden');
    });

    if (maxHeight > 0) {
      const nextHeight = `${Math.ceil(maxHeight)}px`;
      if (this.slider.style.minHeight !== nextHeight) {
        this.slider.style.minHeight = nextHeight;
      }
    }

    this._syncingHeight = false;
  }

  showSlide(index) {
    this.slides.forEach((slide, i) => {
      const isActive = i === index;
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      slide.toggleAttribute('hidden', !isActive);
    });
    this.currentIndex = index;
  }

  next() {
    if (this.isAnimating || this.slides.length < 2) return;
    const nextIndex = (this.currentIndex + 1) % this.slides.length;
    this.transitionTo(nextIndex);
  }

  transitionTo(nextIndex) {
    if (this.isAnimating) return;

    this.isAnimating = true;
    const currentSlide = this.slides[this.currentIndex];
    const nextSlide = this.slides[nextIndex];

    currentSlide.removeAttribute('hidden');
    nextSlide.removeAttribute('hidden');
    currentSlide.setAttribute('aria-hidden', 'false');
    nextSlide.setAttribute('aria-hidden', 'false');
    currentSlide.classList.add('announcement-bar-slider--fade-out-next');
    nextSlide.classList.add('announcement-bar-slider--fade-in-next');

    window.setTimeout(() => {
      currentSlide.classList.remove('announcement-bar-slider--fade-out-next');
      nextSlide.classList.remove('announcement-bar-slider--fade-in-next');
      this.showSlide(nextIndex);
      this.isAnimating = false;
    }, this.animationDelay * 2);
  }

  play() {
    if (!this.autoplayEnabled || this.slides.length < 2 || this.reducedMotion?.matches) return;
    clearInterval(this.timer);
    this.timer = window.setInterval(() => this.next(), this.speed);
  }

  pause() {
    clearInterval(this.timer);
    this.timer = null;
  }
}

if (!customElements.get('announcement-bar-carousel')) {
  customElements.define('announcement-bar-carousel', AnnouncementBarCarousel);
}

function padCountdownValue(value) {
  return String(Math.max(0, value)).padStart(2, '0');
}

function updateAnnouncementCountdown(el) {
  const endAt = Number(el.dataset.end) * 1000;
  if (!Number.isFinite(endAt)) return false;

  const remaining = Math.max(0, endAt - Date.now());
  const days = Math.floor(remaining / 86400000);
  const hours = Math.floor((remaining % 86400000) / 3600000);
  const minutes = Math.floor((remaining % 3600000) / 60000);
  const seconds = Math.floor((remaining % 60000) / 1000);

  const daysEl = el.querySelector('[data-days]');
  const hoursEl = el.querySelector('[data-hours]');
  const minutesEl = el.querySelector('[data-minutes]');
  const secondsEl = el.querySelector('[data-seconds]');

  if (daysEl) daysEl.textContent = days > 99 ? String(days) : padCountdownValue(days);
  if (hoursEl) hoursEl.textContent = padCountdownValue(hours);
  if (minutesEl) minutesEl.textContent = padCountdownValue(minutes);
  if (secondsEl) secondsEl.textContent = padCountdownValue(seconds);

  return remaining > 0;
}

function initAnnouncementCountdowns(root = document) {
  root.querySelectorAll('[data-announcement-countdown]').forEach((el) => {
    if (el.dataset.initialized === 'true') return;
    el.dataset.initialized = 'true';

    const tick = () => {
      const isRunning = updateAnnouncementCountdown(el);
      if (!isRunning && el._countdownTimer) {
        clearInterval(el._countdownTimer);
        el._countdownTimer = null;
      }
    };

    tick();
    el._countdownTimer = window.setInterval(tick, 1000);
  });
}

const startAnnouncementCountdowns = () => initAnnouncementCountdowns();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startAnnouncementCountdowns);
} else {
  startAnnouncementCountdowns();
}

document.addEventListener('shopify:section:load', (event) => {
  initAnnouncementCountdowns(event.target);
});
